import { FileText, LineChart, Globe, MessageSquare, GraduationCap, Lightbulb, PoundSterling, Megaphone, PlayCircle, Filter } from "lucide-react";

// Field types: text (default), select, date, number, textarea, file
// Flags: desc -> maps to the required "description", web -> maps to the "website" column,
// req -> required, opt -> shows "(optional)".
const IMG = "https://static.prod-images.emergentagent.com/jobs/d9a7bc87-c0a9-4970-837a-04fc33441c25/images";

export const SERVICES = [
  {
    id: "business-plans", title: "Business Plans", icon: FileText,
    image: `${IMG}/96d900db5f102923de51a6cc2e2432a6a65dc7ae87a8fc5855f92cada385ff49.png`,
    summary: "We prepare professional business plans for startups, growing businesses, funding applications, grants, investor presentations and business restructuring.",
    fields: [
      { label: "Type of Business" },
      { label: "Purpose of Business Plan", type: "select", options: ["Startup", "Grant Funding", "Loan Application", "Investor Pitch", "CIC", "Franchise", "Other"] },
      { label: "Do you already have financial figures?", type: "select", options: ["Yes", "No"] },
      { label: "Briefly describe what you need", type: "textarea", desc: true },
      { label: "Preferred deadline", type: "date" },
      { label: "Upload supporting document", type: "file", opt: true },
    ],
  },
  {
    id: "cashflow-projections", title: "Cashflow Projections", icon: LineChart,
    image: `${IMG}/46c33455fbb13f3fc62bd566b29e1b37592ffae6593a39fe8e314bf2cb763e79.png`,
    summary: "We create detailed cashflow projections to help businesses plan income, expenses, funding needs, growth targets and financial sustainability.",
    fields: [
      { label: "Projection period needed", type: "select", options: ["12 months", "3 years", "5 years"] },
      { label: "Purpose", type: "select", options: ["Funding", "Grant", "Loan", "Internal Planning", "Investor", "Other"] },
      { label: "Do you have existing accounts?", type: "select", options: ["Yes", "No"] },
      { label: "Estimated monthly income", type: "text" },
      { label: "Estimated monthly expenses", type: "text" },
      { label: "Brief description", type: "textarea", desc: true },
      { label: "Upload figures or accounts", type: "file", opt: true },
    ],
  },
  {
    id: "website-services", title: "Website Services", icon: Globe,
    image: `${IMG}/df64eda36f99bdaf5e0d5af60bf489d9bfee060b180c6fb1e9f1392b4b32dba4.png`,
    summary: "We help businesses create or improve professional websites that clearly explain their services, generate enquiries and support online growth.",
    fields: [
      { label: "What do you need?", type: "select", options: ["New website", "Website redesign", "Website updates", "Landing page", "Other"] },
      { label: "Current website URL", opt: true, web: true },
      { label: "Number of pages required", type: "text" },
      { label: "Main goal of website", type: "select", options: ["Leads", "Sales", "Course Enrolment", "Booking", "Information", "Other"] },
      { label: "Brief description", type: "textarea", desc: true },
      { label: "Preferred deadline", type: "date" },
    ],
  },
  {
    id: "chatbot-design", title: "Chatbot Design", icon: MessageSquare,
    image: `${IMG}/c615e8cf3e71cfa1a29930357bbb0b549a53bd98b0042e72df699874fe11f10d.png`,
    summary: "We design chatbot flows that help businesses answer customer questions, capture leads, collect information and improve response times.",
    fields: [
      { label: "Where will the chatbot be used?", type: "select", options: ["Website", "WhatsApp", "Facebook", "Instagram", "Other"] },
      { label: "Main chatbot purpose", type: "select", options: ["Lead capture", "Customer support", "Bookings", "Course enquiries", "Product recommendations", "Other"] },
      { label: "Do you already have questions and answers prepared?", type: "select", options: ["Yes", "No"] },
      { label: "Brief description of chatbot required", type: "textarea", desc: true },
      { label: "Website or social media link", opt: true, web: true },
    ],
  },
  {
    id: "cpd-courses", title: "CPD Courses", icon: GraduationCap,
    image: `${IMG}/a9758948c72f9f73e4ef0852b109f07fe9d7f0b027e8ae8cd65bc75e938a28e8.png`,
    summary: "We support CPD course creation, course structure, enrolment pages, learner materials and digital course promotion.",
    fields: [
      { label: "Course topic" },
      { label: "What do you need?", type: "select", options: ["Course creation", "CPD setup", "Course sales page", "Learner materials", "Certificate system", "Marketing", "Other"] },
      { label: "Is the course already written?", type: "select", options: ["Yes", "No", "Partly"] },
      { label: "Target learners" },
      { label: "Brief course description", type: "textarea", desc: true },
      { label: "Preferred launch date", type: "date" },
    ],
  },
  {
    id: "business-opportunity", title: "Business Opportunity", icon: Lightbulb,
    image: `${IMG}/0035a71da3c9d488ec077c9b1f71f57a1667048aa14563194c1738ee08b15e93.png`,
    summary: "We help package and present business opportunities, franchises, licensing models, startup offers and growth opportunities in a professional way.",
    fields: [
      { label: "Type of opportunity", type: "select", options: ["Franchise", "Licence", "Partnership", "Investment", "Startup Package", "Other"] },
      { label: "Is the opportunity already trading?", type: "select", options: ["Yes", "No"] },
      { label: "What support do you need?", type: "select", options: ["Brochure", "Business plan", "Financials", "Landing page", "Marketing", "Other"] },
      { label: "Brief description", type: "textarea", desc: true },
      { label: "Target audience" },
      { label: "Preferred deadline", type: "date" },
    ],
  },
  {
    id: "signposting-finance", title: "Signposting to Finance", icon: PoundSterling,
    image: `${IMG}/180481ee49c36877b2cd2cb8e9ed5e97536ad666f4139d747e4c74b4b45d09cb.png`,
    summary: "We help businesses identify possible finance routes, grants, funding options, loan preparation requirements and support documents.",
    fields: [
      { label: "Funding required (estimated amount)" },
      { label: "Purpose of funding" },
      { label: "Business stage", type: "select", options: ["Idea", "Startup", "Trading under 1 year", "Trading over 1 year", "CIC", "Ltd Company", "Sole Trader"] },
      { label: "Do you have accounts?", type: "select", options: ["Yes", "No"] },
      { label: "Do you have a business plan?", type: "select", options: ["Yes", "No"] },
      { label: "Brief description of funding need", type: "textarea", desc: true },
      { label: "Preferred timescale" },
    ],
  },
  {
    id: "virtual-marketing", title: "Virtual Marketing", icon: Megaphone,
    image: `${IMG}/fc4c6868a486942f408ccf9a2b4fb11cc659df033ea838554547d88adecde27b.png`,
    summary: "We provide virtual marketing support to help businesses promote their services online, improve visibility and generate better enquiries.",
    fields: [
      { label: "Marketing support needed", type: "select", options: ["Social media", "Email marketing", "Adverts", "Content planning", "Campaign strategy", "Other"] },
      { label: "Current platforms used" },
      { label: "Target audience" },
      { label: "Main goal", type: "select", options: ["More leads", "More sales", "More bookings", "Brand awareness", "Course enrolments", "Other"] },
      { label: "Brief description", type: "textarea", desc: true },
      { label: "Monthly support or one-off project?", type: "select", options: ["Monthly support", "One-off project"] },
    ],
  },
  {
    id: "explainer-videos", title: "Explainer Videos", icon: PlayCircle,
    image: `${IMG}/668edcc96f51cb1687d2ba1a14efb7d06f245df5ebe24bdc6128e9c26b34eeb4.png`,
    summary: "We create short explainer video concepts, scripts and visual direction to help businesses explain their services, products or courses clearly.",
    fields: [
      { label: "Video type", type: "select", options: ["Business explainer", "Course promo", "Product explainer", "Social media advert", "Avatar video", "Other"] },
      { label: "Approximate length", type: "select", options: ["15 sec", "30 sec", "60 sec", "90 sec", "Other"] },
      { label: "Do you already have a script?", type: "select", options: ["Yes", "No"] },
      { label: "Where will video be used?", type: "select", options: ["Website", "Facebook", "Instagram", "YouTube", "TikTok", "LinkedIn", "Other"] },
      { label: "Brief description", type: "textarea", desc: true },
      { label: "Upload logo or assets", type: "file", opt: true },
    ],
  },
  {
    id: "landing-page-lead-generation", title: "Landing Page Lead Generation", icon: Filter,
    image: `${IMG}/1670cd0b258c9af0ee422f77c3c28bc3001a5c52bcf5c7b1f1d56ea3dc6e163d.png`,
    summary: "We build landing pages designed to capture enquiries, qualify leads and help businesses understand what customers want before calling them back.",
    fields: [
      { label: "Website or social media link", opt: true, web: true },
      { label: "Landing page purpose", type: "select", options: ["Lead capture", "Course enrolment", "Service enquiry", "Product launch", "Event registration", "Other"] },
      { label: "Do you need a form, quiz or smart form?", type: "select", options: ["Form", "Quiz", "Smart Form", "Not sure"] },
      { label: "Target audience" },
      { label: "What information do you want to collect from leads?", type: "textarea" },
      { label: "Do you have existing branding?", type: "select", options: ["Yes", "No"] },
      { label: "Brief description", type: "textarea", desc: true },
      { label: "Preferred deadline", type: "date" },
    ],
  },
];

export const GENERAL_SERVICE = {
  id: "general", title: "General Enquiry", icon: MessageSquare,
  summary: "Not sure what you need? Tell us about your business challenge and we'll guide you to the right service.",
  fields: [
    { label: "What do you need help with?", type: "textarea", desc: true },
    { label: "Preferred contact method", type: "select", options: ["Phone", "Email", "WhatsApp"] },
  ],
};
